import { useContext, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import * as L from 'leaflet';
import { GlobalContext } from '../../App';
import GeoService from '../../services/GeoService';
import { useCC } from '../../contexts/CCContext';

export default function CCRegions() {
  const cc = useCC();
  if (cc?.transport === 'v2') return null;
  return <LegacyCCRegions />;
}

function LegacyCCRegions() {
  const map = useMap();
  const { setGlobalSelectedRegion } = useContext(GlobalContext);
  useEffect(() => {
    const pane = map.getPane('cc-regions') || map.createPane('cc-regions');
    pane.style.zIndex = '350';
    let generation = 0;
    let controller;
    let regions;
    let timer;
    const invalidate = () => {
      generation += 1;
      controller?.abort();
      clearTimeout(timer);
    };
    const load = async () => {
      invalidate();
      const requestId = generation;
      controller = new AbortController();
      const signal = controller.signal;
      const b = map.getBounds();
      try {
        const data = await GeoService.getRegions([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], map.getZoom(), signal);
        if (signal.aborted || generation !== requestId || !Array.isArray(data?.features)) return;
        const nextRegions = L.geoJSON(data, {
          pane: 'cc-regions',
          style: { color: '#666', weight: 1, dashArray: '3 3', fillOpacity: 0 },
          onEachFeature: (feature, layer) => {
            const label = document.createElement('span');
            label.textContent = feature.properties?.name || '';
            layer.bindTooltip(label);
            layer.on('click', () => {
              setGlobalSelectedRegion(feature);
              map.fitBounds(layer.getBounds());
            });
          },
        }).addTo(map).bringToBack();
        // Keep the last successful boundaries visible while their replacement loads.
        if (regions) map.removeLayer(regions);
        regions = nextRegions;
      } catch (error) {
        if (!signal.aborted) console.error('CC region boundaries unavailable; use the region selector', error);
      }
    };
    const schedule = () => { invalidate(); timer = setTimeout(load, 200); };
    map.on('movestart zoomstart', invalidate);
    map.on('moveend zoomend', schedule);
    load();
    return () => {
      invalidate();
      if (regions) map.removeLayer(regions);
      map.off('movestart zoomstart', invalidate);
      map.off('moveend zoomend', schedule);
    };
  }, [map, setGlobalSelectedRegion]);
  return null;
}
